import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { usersApi } from '@/api/endpoints';
import { usePopoverPlacement } from '@/hooks/usePopoverPlacement';
import { matchLocale, SUPPORTED_LOCALES, setLocale, type SupportedLocale } from '@/i18n';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/auth';
import { Segmented } from './ui';

/**
 * 每种语言用它自己的写法显示，不跟着界面翻译 ——
 * 看不懂当前界面的人也得认得出自己的语言，各家登录页都是这么做的。
 */
const LABELS: Record<SupportedLocale, string> = {
  'zh-CN': '中文',
  en: 'English',
  es: 'Español',
};

/**
 * 语言选择：下拉框（CR-056，客户原话「登录和登录后换语言的地方，制作成拉下选择那种标准样式」）。
 *
 * **不用原生 `<select>`**（CR-056 补漏，客户原话「登录后选择语言这个就在移动端出屏幕了。pad」）。
 * 原生下拉的选项列表由浏览器 / 系统摆放：网页端的语言在左侧栏最底下，贴着屏幕底边，
 * pad 上点开列表出了屏幕 —— 那一步我们的代码管不着。所以外观照 `Select` 做（同样的边框、高度、右边的小箭头），
 * 列表自己画，往哪开、多高由 `usePopoverPlacement` 按可视区算（DESIGN-SYSTEM「下拉面板不写死往下开」）。
 *
 * 登录后顺手同步到服务端，这样工人换手机、重装 App 也不用再设一次 —— 语言偏好跟着账号走，不是跟着设备走。
 * 高度是 `touch`：电脑 32、手机 44（CR-082，App 里不再单独放大到 56）。
 */
/**
 * 换语言：本地立刻切，登录了就顺手同步到服务端（语言偏好跟着账号走）。
 * 下拉（登录页）和分段按钮（网页端账号菜单，CR-082）共用。
 */
function useChooseLocale() {
  const { i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  // i18next 的语言可能带区域后缀（zh-CN-x-…、es-MX），归一之后才对得上选项
  const current = matchLocale(i18n.language);
  const choose = (locale: SupportedLocale) => {
    if (locale === current) return;
    setLocale(locale);
    if (user) {
      setUser({ ...user, locale });
      // 同步失败不影响本地已经切好的语言，下次登录会再同步一次
      void usersApi.updateMyLocale(locale).catch(() => undefined);
    }
  };
  return { current, choose };
}

/** 三种语言并排的分段按钮（CR-082 网页端账号菜单里用）：一眼看到有哪几种，点一下就换 */
export function LanguageSegmented() {
  const { t } = useTranslation();
  const { current, choose } = useChooseLocale();
  return (
    <Segmented<SupportedLocale>
      label={t('settings.language')}
      hideLabel
      block
      value={current}
      onChange={choose}
      options={SUPPORTED_LOCALES.map((locale) => ({ value: locale, label: LABELS[locale] }))}
    />
  );
}

export function LanguageSelect({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { current, choose: chooseLocale } = useChooseLocale();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();
  const height = 'min-h-touch';

  usePopoverPlacement(open, rootRef, listRef, { matchWidth: true });

  useEffect(() => {
    if (!open) return;
    // 焦点落到当前那一项，方向键从这里开始走
    listRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus({ preventScroll: true });
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function choose(locale: SupportedLocale) {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
    chooseLocale(locale);
  }

  function onListKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    const items = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [])];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Escape') {
      setOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className={cn('relative shrink-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${t('settings.language')}: ${LABELS[current]}`}
        data-action="settings.language"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        // 外观和 ui 的 Select 一致：同一条边框、同一个聚焦环
        // 最窄也放得下最长的那个（Español）：下拉和按钮一样宽、正好挂在按钮下面。
        // 改之前按钮按「中文」的宽度，下拉比它宽一截，在登录页上伸出卡片边、骑在卡片边框上，看着像被卡片挡住
        className={cn(
          'flex w-full min-w-28 items-center justify-between gap-2 rounded-md border bg-surface pl-3 pr-2 text-body text-ink',
          'transition-colors hover:bg-surface-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
          height,
          open ? 'border-brand-500' : 'border-rule',
        )}
      >
        <span className="whitespace-nowrap">{LABELS[current]}</span>
        <ChevronIcon />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="menu"
          aria-label={t('settings.language')}
          onKeyDown={onListKeyDown}
          // 位置、最大宽高由 usePopoverPlacement 写：侧栏最底下往上开，手机横屏也在屏幕里
          className="absolute left-0 z-dropdown w-full rounded-md border border-rule-strong bg-popover py-1 shadow-pop"
        >
          {SUPPORTED_LOCALES.map((locale) => {
            const selected = locale === current;
            return (
              <li key={locale} role="none">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => choose(locale)}
                  className={cn(
                    'flex w-full items-center justify-between gap-4 whitespace-nowrap px-3 text-left text-body',
                    'hover:bg-ink/6 focus:bg-ink/6 focus:outline-none',
                    height,
                    selected ? 'font-medium text-brand-700' : 'text-ink',
                  )}
                >
                  {LABELS[locale]}
                  {selected && <CheckIcon />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** 向下的小箭头，和原生下拉右边那个一样的位置。`currentColor`，不写色值 */
function ChevronIcon() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="h-4 w-4 shrink-0 text-ink-3" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}
